<template>
    <div>
        <table class="tracking-listing">
            <thead>
                <tr>
                    <th v-if="!isTablet">{{ createdReceivedLabel }}</th>
                    <th>{{ physicianHeading }}</th>
                    <th v-if="!isTablet">{{ $t('pages.referrals.index.headings.patient') }}</th>
                    <th v-if="!isTablet">{{ $t('pages.referrals.index.headings.status') }}</th>
                    <th></th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(referral, index) in referrals" :key="index">
                    <td v-if="!isTablet">
                        <listing-datetime
                            :datetime="referral.createdAt"
                            :icon="getAppointmentDateIcon(referral)"
                        ></listing-datetime>
                    </td>
                    <td>
                        <div v-if="type === ReferralPathTypeEnum.RECEIVED || referral.numberOfProviders === 1">
                            <practice-details
                                v-if="showPracticeDetails(referral)"
                                :practice="getPracticeDetails(referral)"
                            ></practice-details>
                            <user-details
                                v-else-if="!isFromPatient"
                                :user="physicianData(referral)"
                                :bottomText="physicianSpeciality(referral)"
                                :bottomText2="getCreatedDate(referral)"
                            ></user-details>
                            <patient-details
                                v-else
                                :user="referral?.referralData?.patient"
                                :bottomText2="getCreatedDate(referral)"
                            ></patient-details>
                        </div>
                        <div v-else>
                            <span>
                                <common-button
                                    :label="`${referral.numberOfProviders} selected providers`"
                                    :isSubmittable="false"
                                    type="link"
                                    @click="openProvidersModal(referral.id)"
                                ></common-button>
                            </span>
                        </div>
                    </td>

                    <td v-if="!isTablet">
                        <div class="tracking-listing__patient">
                            <div :class="patientDataClass(referral)">
                                <p class="user-details-card__highlited" @click="openReferralInfoModal(referral)">
                                    {{ getUserFullName(referral?.referralData?.patient) }}
                                </p>
                                <p>{{ $t('pages.referrals.index.patient.dob') }}</p>
                                <p>{{ getPatientDateOfBirth(referral?.referralData?.patient) }}</p>
                            </div>
                            <div class="tracking-listing__patient--tooltip">
                                <common-info-tip
                                    v-if="!referral.shouldShowPatientInfo"
                                    class="tracking-listing__patient--tooltip-text"
                                >
                                    {{ $t('pages.referrals.index.patient.tooltip') }}
                                </common-info-tip>
                            </div>
                        </div>
                    </td>

                    <td v-if="!isTablet">
                        <referrals-status :referral="referral"></referrals-status>
                    </td>

                    <td class="listing-listing__action">
                        <referrals-status v-if="isTablet" :showDescriptionText="false" :referral="referral"></referrals-status>
                        <listing-action :type="$route.params.type" :referral="referral"></listing-action>
                    </td>
                </tr>
            </tbody>
        </table>
        <referrals-patient-details-modal
            v-if="showPatientDetailsModal"
            :referral="activeReferral"
            @close="showPatientDetailsModal = false"
        ></referrals-patient-details-modal>
        <referrals-pool-providers-modal
            v-if="showReferralsPoolProvidersModal"
            :referralPoolId="activeReferralPoolId"
            @close="closeProvidersModal"
        ></referrals-pool-providers-modal>
    </div>
</template>

<script>
    import dayjs from 'dayjs'
    import ReferralsStatus from 'components/page-elements/ReferralsStatus'
    import UserDetails from 'components/page-elements/referrals/details/UserDetailsCard'
    import PatientDetails from 'components/page-elements/referrals/details/PatientDetailsCard'
    import PracticeDetails from 'components/page-elements/referrals/details/PracticeDetailsCard'
    import ListingDatetime from 'components/page-elements/referrals-index-listing/ListingDatetime'
    import ListingAction from 'components/page-elements/referrals-index-listing/ListingAction'
    import DateTimeMixin from 'mixins/DateTimeMixin'
    // import ReferralsAPI from 'api/ReferralsAPI'
    import ReferralsPoolAPI from 'api/ReferralsPoolAPI'
    import ReferralsPatientDetailsModal from 'components/page-elements/referrals/ReferralsPatientDetailsModal'
    import ReferralsPoolProvidersModal from 'components/page-elements/referrals/ReferralsPoolProvidersModal'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'
    import ReferralPathTypeEnum from '@/enums/ReferralPathTypeEnum'
    import ScreenSizeDetectionMixin from 'mixins/ScreenSizeDetectionMixin'

    export default {
        mixins: [DateTimeMixin, ScreenSizeDetectionMixin],

        components: {
            ReferralsStatus,
            UserDetails,
            PatientDetails,
            ListingDatetime,
            ListingAction,
            ReferralsPatientDetailsModal,
            PracticeDetails,
            ReferralsPoolProvidersModal,
        },

        props: {
            type: { type: String, required: true },
            referrals: { type: Array, required: true },
            filterOptions: { type: Array, required: true },
            isFromPatient: { type: Boolean, required: false, default: false },
        },

        data() {
            return {
                showPatientDetailsModal: false,
                activeReferral: {},
                PracticeTypeEnum: PracticeTypeEnum,
                practiceData: {},
                ReferralPathTypeEnum: ReferralPathTypeEnum,
                showReferralsPoolProvidersModal: false,
                activeReferralPoolId: null,
            }
        },

        computed: {
            physicianHeading() {
                return this.$t(
                    this.type === ReferralPathTypeEnum.RECEIVED
                        ? 'pages.referrals.index.headings.from'
                        : 'pages.referrals.index.headings.to',
                )
            },

            createdReceivedLabel() {
                return this.$t(
                    this.type === ReferralPathTypeEnum.RECEIVED
                        ? 'pages.referrals.index.headings.created_at'
                        : 'pages.referrals.index.headings.created_at',
                )
            },
        },

        methods: {
            showPracticeDetails(referral) {
                if (this.type === ReferralPathTypeEnum.RECEIVED) {
                    return referral.fromPractice?.practiceType === PracticeTypeEnum.ANCILLARY
                } else {
                    return !!referral?.toPractice
                }
            },
            getPracticeDetails(referral) {
                if (this.type === ReferralPathTypeEnum.RECEIVED) {
                    return referral.fromPractice
                } else {
                    return referral.toPractice
                }
            },
            physicianData(referral) {
                return this.type === ReferralPathTypeEnum.RECEIVED ? referral.fromUser : referral.toUser
            },
            physicianSpeciality(referral) {
                if (referral.toUser?.specialities || referral.fromUser?.specialities) {
                    return this.$auth.getUser().id === referral.fromUser?.id
                        ? referral.toUser?.specialities[0]?.speciality?.name
                        : referral.fromUser?.specialities[0]?.speciality?.name
                }
            },
            getAppointmentDateIcon(referral) {
                const icon = {
                    name: 'white-warning',
                    size: 'medium',
                    hintHeader: this.$t('pages.referrals.index.warnings.reschedule.hintTitle'),
                    hintText: this.$t('pages.referrals.index.warnings.reschedule.text'),
                }
                const isReschedule = referral.status === 'RESCHEDULE_AVAILABILITY_CHANGE'
                const appointmentDateTime = dayjs(referral.appointmentDateTime)
                const isInNext24h = dayjs().isBefore(appointmentDateTime) && dayjs().add(24, 'h').isAfter(appointmentDateTime)
                return isReschedule && isInNext24h ? icon : null
            },

            getCreatedDate(referral) {
                return dayjs(referral.createdDateTime).format('MMMM D, YYYY')
            },

            openReferralInfoModal(referral) {
                const { id: activeMemberId } = this.$auth.getActiveMember()
                ReferralsPoolAPI.getReferralPoolById(referral.id, this.$route.params.type, activeMemberId).then((response) => {
                    this.activeReferral = response
                    this.showPatientDetailsModal = true
                })
            },

            getUserFullName(patient) {
                return `${patient?.firstName} ${patient?.lastName}`
            },

            getPatientDateOfBirth(patient) {
                return dayjs(patient?.dateOfBirth).format('MM/DD/YYYY')
            },

            patientDataClass(referral) {
                return {
                    'text-blured': !referral.shouldShowPatientInfo,
                }
            },

            openProvidersModal(id) {
                this.activeReferralPoolId = id
                this.showReferralsPoolProvidersModal = true
            },

            closeProvidersModal() {
                this.activeReferralPoolId = null
                this.showReferralsPoolProvidersModal = false
            },
        },
    }
</script>
